(() => {
  'use strict';

  const FAVORITES_KEY = 'edelmanFavorites';
  const CONFIGURATIONS_KEY = 'edelmanConfigurations';
  const i18n = window.EdelmanI18n || {};
  const rootPath = window.Shopify?.routes?.root || '/';
  const storefrontUrl = path => `${rootPath}${String(path || '').replace(/^\/+/, '')}`;

  const read = key => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const write = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(Array.isArray(value) ? value : []));
      return true;
    } catch (error) {
      document.querySelectorAll('[data-favorites-list], [data-config-list]').forEach(root => { root.textContent = i18n.storageError || ''; });
      return false;
    }
  };

  const escapeHtml = value => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const safeStoreUrl = (value, fallback = '') => {
    try {
      const url = new URL(value || fallback, window.location.origin);
      if (url.origin !== window.location.origin) return fallback || rootPath;
      const path = url.pathname.replace(/^\/(?:en|de|fr|it|es|nl)(?=\/|$)/, '');
      return `${storefrontUrl(path)}${url.search}${url.hash}`;
    } catch (error) {
      return fallback || rootPath;
    }
  };

  const safeImageUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  };

  function syncFavoriteButtons() {
    const favorites = read(FAVORITES_KEY);
    document.querySelectorAll('[data-edelman-favorite]').forEach(button => {
      const active = favorites.some(item => item.handle === button.dataset.handle);
      button.classList.toggle('is-active', active);
      button.textContent = active ? '♥' : '♡';
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? i18n.removeFavorite : i18n.addFavorite);
    });
  }

  function favoriteCard(item) {
    const handle = escapeHtml(item.handle || '');
    const title = escapeHtml(item.title || i18n.watchFallback);
    const price = escapeHtml(item.price || '');
    const image = safeImageUrl(item.image);
    const url = safeStoreUrl(item.url, storefrontUrl('collections/edelman'));

    return `<article class="edelman-saved-card">
      ${image ? `<img src="${escapeHtml(image)}" alt="${title}" loading="lazy">` : '<div class="edelman-saved-card__placeholder" aria-hidden="true"></div>'}
      <div><strong>${title}</strong><div>${price}</div></div>
      <div class="edelman-saved-card__actions">
        <a class="edelman-button edelman-button--ghost" href="${escapeHtml(url)}">${escapeHtml(i18n.view)}</a>
        <button class="edelman-saved-remove" type="button" data-remove-favorite="${handle}">${escapeHtml(i18n.remove)}</button>
      </div>
    </article>`;
  }

  function configurationCard(item) {
    const code = escapeHtml(item.code || '');
    let displayName = item.name || i18n.savedDesign;
    if (item.state && window.EdelmanDesignCore) {
      try {
        const labels = JSON.parse(document.querySelector('[data-saved-design-labels]')?.textContent || '{}');
        const state = window.EdelmanDesignCore.migrate(item.state);
        const model = labels[state.model], dial = model?.dials?.[state.dial];
        if (dial) displayName = `${model.name} ${dial}`;
      } catch {}
    }
    const name = escapeHtml(displayName);
    let value = item.url;
    if (item.state && window.EdelmanDesignCore) value = window.EdelmanDesignCore.url(new URL(storefrontUrl('pages/custom-horloges'), location.origin).href, window.EdelmanDesignCore.migrate(item.state));
    const url = safeStoreUrl(value, storefrontUrl('pages/custom-horloges'));

    return `<article class="edelman-saved-card">
      <div class="edelman-saved-card__placeholder" aria-hidden="true"></div>
      <div><strong>${name}</strong><div>${code}</div></div>
      <div class="edelman-saved-card__actions">
        <a class="edelman-button edelman-button--ghost" href="${escapeHtml(url)}">${escapeHtml(i18n.open)}</a>
        <button class="edelman-saved-remove" type="button" data-remove-config="${code}">${escapeHtml(i18n.remove)}</button>
      </div>
    </article>`;
  }

  function renderHub() {
    document.querySelectorAll('[data-favorites-list]').forEach(root => {
      const items = read(FAVORITES_KEY);
      root.innerHTML = items.length
        ? `${items.map(favoriteCard).join('')}<button class="edelman-clear-saved" type="button" data-clear-favorites>${escapeHtml(i18n.clearFavorites)}</button>`
        : `<p class="edelman-empty">${escapeHtml(i18n.emptyFavorites)}</p>`;
    });

    document.querySelectorAll('[data-config-list]').forEach(root => {
      const items = read(CONFIGURATIONS_KEY);
      root.innerHTML = items.length
        ? `${items.map(configurationCard).join('')}<button class="edelman-clear-saved" type="button" data-clear-configs>${escapeHtml(i18n.clearConfigs)}</button>`
        : `<p class="edelman-empty">${escapeHtml(i18n.emptyConfigs)}</p>`;
    });
  }

  document.addEventListener('click', event => {
    const favoriteButton = event.target.closest('[data-edelman-favorite]');
    if (favoriteButton) {
      let favorites = read(FAVORITES_KEY);
      const item = {
        handle: favoriteButton.dataset.handle,
        title: favoriteButton.dataset.title,
        url: favoriteButton.dataset.url,
        image: favoriteButton.dataset.image,
        price: favoriteButton.dataset.price
      };

      favorites = favorites.some(saved => saved.handle === item.handle)
        ? favorites.filter(saved => saved.handle !== item.handle)
        : [item, ...favorites].slice(0, 40);

      if (!write(FAVORITES_KEY, favorites)) { favoriteButton.setAttribute('aria-label', i18n.storageError); return; }
      syncFavoriteButtons();
      renderHub();
      document.dispatchEvent(new Event('edelman:saved-change'));
      return;
    }

    const removeFavorite = event.target.closest('[data-remove-favorite]');
    if (removeFavorite) {
      if (!write(FAVORITES_KEY, read(FAVORITES_KEY).filter(item => item.handle !== removeFavorite.dataset.removeFavorite))) return;
      syncFavoriteButtons();
      renderHub();
      return;
    }

    const removeConfig = event.target.closest('[data-remove-config]');
    if (removeConfig) {
      if (!write(CONFIGURATIONS_KEY, read(CONFIGURATIONS_KEY).filter(item => String(item.code || '') !== removeConfig.dataset.removeConfig))) return;
      renderHub();
      return;
    }

    if (event.target.closest('[data-clear-favorites]')) {
      if (!write(FAVORITES_KEY, [])) return;
      syncFavoriteButtons();
      renderHub();
      return;
    }

    if (event.target.closest('[data-clear-configs]')) {
      if (!write(CONFIGURATIONS_KEY, [])) return;
      renderHub();
    }
  });


  document.addEventListener('edelman:saved-change', renderHub);
  window.addEventListener('storage', event => {
    if ([FAVORITES_KEY, CONFIGURATIONS_KEY].includes(event.key)) {
      syncFavoriteButtons();
      renderHub();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    syncFavoriteButtons();
    renderHub();
  });
})();
